import {
  chebyshevToRect,
  type CollisionGrid,
  minStepIntervalMs,
  ServerClock,
  step,
  stepTilesPerSecond,
  type Direction,
  type TilePos,
} from '@veriverse/game-logic';
import {
  buildCollisionGrid,
  DEV_TILESET_NAME,
  getRegions,
  regionAt,
  RENDERED_TILE_LAYERS,
  type Region,
  type TiledMap,
} from '@veriverse/map-format';
import {
  CLIENT_MESSAGES,
  interactionFor,
  localDate,
  type CalendarBirthday,
  type CalendarMonthResponse,
  type ChatEvent,
  type InteractMessage,
  type MoveMessage,
  type ClientWeatherCondition,
  type OfficeLocation,
  type PlayerStatus,
  type RosterEntry,
  TILE_SIZE,
} from '@veriverse/shared';
import { type Room } from 'colyseus.js';
import Phaser from 'phaser';
import { clientConfig } from '../../config.js';
import { api } from '../../net/api.js';
import { officeStateCallbacks } from '../../net/state-callbacks.js';
import { createEmoteAnimations, emoteEvents, loadEmotes } from '../emotes.js';
import { emoteWheelOpen } from '../../state/emote-wheel.js';
import { typingEvents } from '../../net/typing-indicator.js';
import { celebrantIdsToday } from '../../state/birthday-calendar.js';
import { parseObjectData } from '../object-events.js';
import { pinDoorPress, usePinDoorStore } from '../../state/pin-door.js';
import { useBoardStore } from '../../state/board-store.js';
import { useSessionStore } from '../../state/session-store.js';
import { OFFICE_TIME_ZONE } from '../../state/welcome-weather.js';
import { prefersReducedMotion, useSettingsStore } from '../../state/settings.js';
import { clickPath, directionToward } from '../click-to-move.js';
import { Avatar } from '../avatar.js';
import { loadBirthdayArt } from '../birthday-art.js';
import {
  birthdayDecorations,
  cakeSpots,
  deskAreas,
  devBirthdayOverride,
  type DeskOwner,
} from '../birthday-decor.js';
import { BirthdayLayer } from '../birthday-layer.js';
import { LIGHTING_UPDATE_MS, LightingLayer } from '../lighting-layer.js';
import { weatherEffects } from '../weather-effects.js';
import { FogLayer } from '../fog-layer.js';
import { WeatherLayer } from '../weather-layer.js';
import { devTimeOverride, devWeatherOverride, themeParamOverride } from '../lighting-math.js';
import { shownTheme } from '../theme-effects.js';
import { ThemeLayer } from '../theme-layer.js';
import { addAnimatedObjects, loadAnimatedObjects } from '../animated-objects.js';
import { AnimatedTiles } from '../animated-tiles.js';
import { cameraBounds, sameBounds, type CameraBounds } from '../camera-region.js';
import { weatherFor } from '../../state/hud-weather.js';
import { addSpriteObjects } from '../sprite-objects.js';
import { addCatScholes, loadCatScholes } from '../cat-scholes.js';
import { addCoopDoors, loadDoorArt } from '../coop-doors.js';
import { createCharacterAnimations, loadCharacterSheets } from '../characters.js';
import { loadSprintDust } from '../sprint-dust.js';
import { createPlaceholderTextures } from '../textures.js';
import {
  findInteractionTarget,
  interactiveObjects,
  type InteractiveObject,
} from '../interaction-target.js';
import { PromptBubble } from '../prompt-bubble.js';
import { isTyping } from '../typing.js';
import { integerZoom } from '../zoom.js';

export const WORLD_SCENE_KEY = 'world';
const MAP_KEY = 'office-map';

/** Texture key of an uploaded tileset image (`{hash}.png`). The dev tileset keeps its own key. */
const tilesetTextureKey = (image: string): string =>
  image === `${DEV_TILESET_NAME}.png` ? DEV_TILESET_NAME : `tileset:${image}`;

/** How often today's birthdays are re-read (they change at midnight or when someone edits theirs). */
const BIRTHDAY_REFRESH_MS = 10 * 60_000;

/** Must match the server's MOVE_TILES_PER_SECOND; served by /api/meta in a later phase. */
const TILES_PER_SECOND = 4;

/** Outline around the interactive object under the mouse: over the night shade and lamp glow. */
const HOVER_DEPTH = 1650;
const HOVER_COLOR = 0xd4a017;

const LAYER_DEPTH: Record<(typeof RENDERED_TILE_LAYERS)[number], number> = {
  ground: 0,
  walls: 1,
  decor_below: 2,
  decor_above: 1000,
};

/** Shape of a synced player as decoded on the client (mirrors the server `Player` schema). */
interface PlayerView {
  userId: string;
  name: string;
  x: number;
  y: number;
  dir: Direction;
  seq: number;
  sprinting: boolean;
  appearanceKey: string;
  status: PlayerStatus;
  activity: string;
}

/** The weather part of the decoded room state (mirrors the server `WeatherState`). */
interface WeatherView {
  condition?: ClientWeatherCondition;
  precipitationMmH?: number;
  windMs?: number;
}

interface WorldSceneData {
  room: Room;
  /** The live Tiled map, already validated by the server. */
  map: TiledMap;
  /** Where the office is (from `/api/meta`): drives the real-sun day/night cycle. */
  office: OfficeLocation;
}

/**
 * The office world: renders the map and players, reads input, predicts the local player's
 * movement and reconciles it with the authoritative server state (spec §6.4, §9).
 */
export class WorldScene extends Phaser.Scene {
  private room!: Room;
  private mapData!: TiledMap;
  private office!: OfficeLocation;
  private readonly clock = new ServerClock();
  private lighting: LightingLayer | undefined;
  private weather: WeatherLayer | undefined;
  private theme: ThemeLayer | undefined;
  private animatedTiles: AnimatedTiles | undefined;
  private birthdayLayer: BirthdayLayer | undefined;
  /** User ids celebrating a birthday today (spec §6.13): they wear a party hat. */
  private celebrants: ReadonlySet<string> = new Set();
  /** Session id → user id of every avatar, for the party hats. */
  private readonly avatarUsers = new Map<string, string>();
  private serverThemeId = '';
  /** Last applied weather reading, so state patches that do not touch it change nothing. */
  private weatherKey = '';
  private grid!: CollisionGrid;
  /** Separate areas of the map (e.g. two offices); the camera stays in the current one. */
  private regions: Region[] = [];
  private bounds: CameraBounds | undefined;
  /** Name of the region the local player is in; its weather is shown. */
  private regionName: string | undefined;
  private readonly avatars = new Map<string, Avatar>();
  private keys!: Record<Direction, Phaser.Input.Keyboard.Key[]>;
  private interactKey!: Phaser.Input.Keyboard.Key;
  private sprintKey!: Phaser.Input.Keyboard.Key;
  /** Tiles still to walk after a click (spec §14 click-to-move). */
  private path: TilePos[] = [];
  private hover: Phaser.GameObjects.Graphics | undefined;
  private promptBubble: PromptBubble | undefined;
  private hovered: InteractiveObject | undefined;
  private interactives: InteractiveObject[] = [];
  /** Where the local player looks: the last direction pressed (picks between nearby objects). */
  private facing: Direction = 'down';
  /** Tile and facing the interaction target was last computed for. */
  private targetKey = '';
  private seq = 0;
  private disposed = false;
  private readonly disposers: (() => void)[] = [];
  private lastStepAt = 0;
  private readonly stepMs = minStepIntervalMs(TILES_PER_SECOND);
  private readonly sprintStepMs = minStepIntervalMs(stepTilesPerSecond(TILES_PER_SECOND, true));

  constructor() {
    super(WORLD_SCENE_KEY);
  }

  init(data: WorldSceneData): void {
    this.room = data.room;
    this.mapData = data.map;
    this.office = data.office;
  }

  preload(): void {
    this.cache.tilemap.add(MAP_KEY, {
      format: Phaser.Tilemaps.Formats.TILED_JSON,
      data: this.mapData,
    });
    // Uploaded tilesets live on the API server (another origin), so the request must use CORS.
    this.load.crossOrigin = 'anonymous';
    for (const set of this.mapData.tilesets) {
      if (set.image === `${DEV_TILESET_NAME}.png`) continue; // painted at runtime
      this.load.image(
        tilesetTextureKey(set.image),
        `${clientConfig.serverUrl}/assets/${set.image}`,
      );
    }
    loadCharacterSheets(this);
    loadAnimatedObjects(this, this.mapData);
    loadDoorArt(this, this.mapData);
    loadBirthdayArt(this);
    loadCatScholes(this);
    loadSprintDust(this);
    loadEmotes(this);
    this.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, (file: Phaser.Loader.File) => {
      console.error(`Could not load image ${file.url as string}`);
    });
  }

  create(): void {
    // React StrictMode may destroy a game before its first scene finishes booting.
    if (isTearingDown(this.sys.game)) return;
    createPlaceholderTextures(this);
    createCharacterAnimations(this, prefersReducedMotion());
    createEmoteAnimations(this);
    this.grid = buildCollisionGrid(this.mapData);
    this.regions = getRegions(this.mapData);
    this.interactives = interactiveObjects(this.mapData);

    const map = this.make.tilemap({ key: MAP_KEY });
    // A tileset whose image failed to load is skipped: its tiles stay blank, the rest renders.
    const tilesets = this.mapData.tilesets.flatMap((set) => {
      const key = tilesetTextureKey(set.image);
      if (!this.textures.exists(key)) return [];
      const added = map.addTilesetImage(set.name, key);
      return added ? [added] : [];
    });
    if (tilesets.length === 0) throw new Error('None of the map tilesets could be loaded');
    const layers = RENDERED_TILE_LAYERS.flatMap((name) => {
      const layer = map.createLayer(name, tilesets, 0, 0)?.setDepth(LAYER_DEPTH[name]);
      return layer ? [layer] : [];
    });
    this.animatedTiles = new AnimatedTiles(this.mapData, layers, prefersReducedMotion());
    addSpriteObjects(this, this.mapData, tilesetTextureKey);
    addAnimatedObjects(this, this.mapData, prefersReducedMotion());
    const fog = new FogLayer(this, this.mapData, prefersReducedMotion());
    this.disposers.push(() => fog.destroy());
    addCatScholes(
      this,
      this.mapData,
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    );
    const doors = addCoopDoors(this, this.mapData, this.grid, prefersReducedMotion());
    if (doors.count > 0) {
      const onDoors = (state: unknown) => {
        if (this.alive()) doors.sync(state);
      };
      this.room.onStateChange(onDoors);
      this.disposers.push(() => this.room.onStateChange.remove(onDoors));
    }
    this.setupLighting();
    this.setupBirthdays();

    const camera = this.cameras.main;
    camera.setBounds(0, 0, map.widthInPixels, map.heightInPixels);
    this.bounds = undefined;
    camera.setBackgroundColor('#14161e');
    camera.setRoundPixels(true);
    this.applyZoom();
    this.scale.on(Phaser.Scale.Events.RESIZE, this.applyZoom, this);

    this.setupInput();
    // Clicking the world takes keyboard focus back from the chat box, and walks there.
    this.input.on(Phaser.Input.Events.POINTER_DOWN, (pointer: Phaser.Input.Pointer) => {
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
      if (pointer.leftButtonDown()) this.walkTo(pointer);
    });
    this.hover = this.add.graphics().setDepth(HOVER_DEPTH);
    this.promptBubble = new PromptBubble(this);
    this.input.on(Phaser.Input.Events.POINTER_MOVE, this.updateHover, this);
    this.input.on(Phaser.Input.Events.GAME_OUT, () => this.setHovered(undefined));
    this.bindRoomState();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.dispose());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.dispose());
  }

  override update(time: number, delta: number): void {
    if (!this.disposed) {
      this.weather?.update(time, delta);
      this.theme?.update(time, delta);
      this.animatedTiles?.update(time);
      this.updateCameraBounds();
    }
    if (this.disposed || !this.keys) return;
    this.updateInteractionTarget();
    // Always consume the key press, so an "e" typed in the chat box does not fire later.
    const interactPressed = Phaser.Input.Keyboard.JustDown(this.interactKey);
    if (isTyping() || emoteWheelOpen()) return;
    if (interactPressed) this.interact();
    const held = this.heldDirection();
    if (held) this.path = []; // the keyboard always wins over a click
    const self = this.avatars.get(this.room.sessionId);
    const direction = held ?? (self && this.pathDirection(self.tile));
    if (direction) {
      this.facing = direction;
      self?.setFacing(direction);
    }
    // Holding the sprint key (Space by default) walks faster, by keyboard or along a click path.
    const sprint = this.sprintKey.isDown;
    const stepMs = sprint ? this.sprintStepMs : this.stepMs;
    if (!direction || time - this.lastStepAt < stepMs) return;
    if (!self) return;

    // Prediction: move immediately if the target looks walkable; the server has the final word.
    const target = step(self.tile, direction);
    const message: MoveMessage = { seq: ++this.seq, dir: direction, ...(sprint && { sprint }) };
    this.room.send(CLIENT_MESSAGES.move, message);
    this.lastStepAt = time;
    if (this.grid.isWalkable(target)) self.moveTo(target.x, target.y, stepMs, sprint);
    if (!held) this.path.shift();
  }

  /**
   * Keeps the camera inside the region the local player is in. Entering another region (a
   * teleport) jumps the camera there instead of panning across the map.
   */
  private updateCameraBounds(): void {
    const self = this.avatars.get(this.room.sessionId);
    if (!self || this.regions.length === 0) return;
    const region = regionAt(this.regions, self.tile)?.name;
    if (region !== this.regionName) {
      this.regionName = region;
      useSessionStore.getState().setRegion(region);
      this.applyWeather(weatherFor<WeatherView>(this.room.state, region));
    }
    const bounds = cameraBounds(this.regions, self.tile, this.mapData.width, this.mapData.height);
    if (sameBounds(this.bounds, bounds)) return;
    this.bounds = bounds;
    const camera = this.cameras.main;
    // A region smaller than the view is centred (Phaser would pin it to the top-left corner and
    // show what lies beside it).
    const padX = Math.max(0, (camera.width / camera.zoom - bounds.width) / 2);
    const padY = Math.max(0, (camera.height / camera.zoom - bounds.height) / 2);
    camera.setBounds(
      bounds.x - padX,
      bounds.y - padY,
      bounds.width + 2 * padX,
      bounds.height + 2 * padY,
    );
    camera.centerOn(self.container.x, self.container.y);
  }

  /** The next step of a click-to-move path, or `undefined` (and the path dropped) if off it. */
  private pathDirection(from: TilePos): Direction | undefined {
    const next = this.path[0];
    if (!next) return undefined;
    const direction = directionToward(from, next);
    if (!direction) this.path = []; // the server put us somewhere else: stop
    return direction;
  }

  private pointerTile(pointer: Phaser.Input.Pointer): TilePos {
    return {
      x: Math.floor(pointer.worldX / TILE_SIZE),
      y: Math.floor(pointer.worldY / TILE_SIZE),
    };
  }

  private interactiveAt(tile: TilePos): InteractiveObject | undefined {
    return this.interactives.find(({ object }) => chebyshevToRect(tile, object.footprint) === 0);
  }

  private walkTo(pointer: Phaser.Input.Pointer): void {
    const self = this.avatars.get(this.room.sessionId);
    if (!self || !this.grid) return;
    const clicked = this.pointerTile(pointer);
    const object = this.interactiveAt(clicked)?.object.footprint;
    this.path = clickPath(this.grid, self.tile, clicked, object) ?? [];
  }

  /** Outlines the interactive object under the mouse and shows a hand cursor (spec §14). */
  private updateHover(pointer: Phaser.Input.Pointer): void {
    this.setHovered(this.interactiveAt(this.pointerTile(pointer)));
  }

  private setHovered(item: InteractiveObject | undefined): void {
    if (item === this.hovered || !this.hover) return;
    this.hovered = item;
    this.hover.clear();
    this.input.setDefaultCursor(item ? 'pointer' : '');
    if (!item) return;
    const { x, y, w, h } = item.object.footprint;
    this.hover.lineStyle(1, HOVER_COLOR, 1);
    this.hover.strokeRect(
      x * TILE_SIZE + 0.5,
      y * TILE_SIZE + 0.5,
      w * TILE_SIZE - 1,
      h * TILE_SIZE - 1,
    );
  }

  /**
   * Detaches everything that outlives the scene: server callbacks and store subscriptions.
   * Needed because the room outlives the Phaser game (React StrictMode creates and destroys a
   * game in development), and a dead scene must never touch its renderer.
   */
  /** False once the scene is disposed or its game is being torn down (Phaser destroys lazily). */
  private alive(): boolean {
    return !this.disposed && !isTearingDown(this.sys.game);
  }

  private dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    useSessionStore.getState().setInteractionTarget(undefined);
    this.promptBubble?.destroy();
    this.promptBubble = undefined;
    for (const dispose of this.disposers) dispose();
    this.disposers.length = 0;
    this.scale?.off(Phaser.Scale.Events.RESIZE, this.applyZoom, this);
  }

  /**
   * Real-sun day/night (spec §6.10) on server-synced time, refreshed every 10 s, plus the office
   * weather from the room state (spec §6.11).
   */
  private setupLighting(): void {
    const preview = devTimeOverride(window.location.search, import.meta.env.DEV);
    const onState = (state: unknown) => {
      if (!this.alive()) return;
      const view = state as { serverTime?: number } | undefined;
      this.clock.sync(view?.serverTime ?? 0);
      this.applyWeather(weatherFor<WeatherView>(state, this.regionName));
      const themeId = (state as { themeId?: string } | undefined)?.themeId ?? '';
      if (themeId !== this.serverThemeId) {
        this.serverThemeId = themeId;
        this.applyTheme();
      }
    };
    this.room.onStateChange(onState);
    this.disposers.push(() => this.room.onStateChange.remove(onState));

    this.lighting = new LightingLayer(
      this,
      this.mapData,
      this.office,
      () => preview ?? this.clock.now(),
      useSettingsStore.getState().darkness / 100,
    );
    this.disposers.push(
      useSettingsStore.subscribe((current, previous) => {
        if (current.darkness !== previous.darkness && this.alive())
          this.lighting?.setIntensity(current.darkness / 100);
      }),
    );
    this.weather = new WeatherLayer(this, this.mapData);
    this.theme = new ThemeLayer(this);
    this.applyTheme();
    // An admin's local preview (Admin console → Themes) swaps the theme in place.
    this.disposers.push(
      useSessionStore.subscribe((current, previous) => {
        if (current.themePreview !== previous.themePreview && this.alive()) this.applyTheme();
      }),
    );
    onState(this.room.state);
    this.lighting.update();
    this.time.addEvent({
      delay: LIGHTING_UPDATE_MS,
      loop: true,
      callback: () => this.lighting?.update(),
    });
  }

  /**
   * Birthday decorations (spec §6.13): a party hat on everyone celebrated today, balloons on their
   * desks and a cake at the kitchen's cake spot. Re-read every few minutes and whenever the user
   * edits their own birthday.
   */
  private setupBirthdays(): void {
    this.birthdayLayer = new BirthdayLayer(this);
    void this.refreshBirthdays();
    this.time.addEvent({
      delay: BIRTHDAY_REFRESH_MS,
      loop: true,
      callback: () => void this.refreshBirthdays(),
    });
    this.disposers.push(
      useSessionStore.subscribe((current, previous) => {
        if (current.user?.birthday !== previous.user?.birthday && this.alive())
          void this.refreshBirthdays();
      }),
    );
  }

  private async refreshBirthdays(): Promise<void> {
    const user = useSessionStore.getState().user;
    if (!user) return;
    const today = localDate(new Date(), OFFICE_TIME_ZONE);
    const preview = devBirthdayOverride(window.location.search, import.meta.env.DEV);
    const me = {
      id: user.id,
      displayName: user.displayName,
      birthday: preview ? { month: today.month, day: today.day } : user.birthday,
    };
    // Either request may fail; the user's own birthday still shows without them.
    const [birthdays, roster] = await Promise.all([
      api<CalendarMonthResponse>(`/api/calendar?year=${today.year}&month=${today.month}`)
        .then((body) => body.birthdays)
        .catch((): CalendarBirthday[] => []),
      api<RosterEntry[]>('/api/roster').catch((): DeskOwner[] => [
        { userId: user.id, deskObjectId: user.deskObjectId },
      ]),
    ]);
    if (!this.alive()) return;
    this.celebrants = celebrantIdsToday(me, birthdays, today);
    for (const [sessionId, avatar] of this.avatars)
      avatar.setPartyHat(this.celebrants.has(this.avatarUsers.get(sessionId) ?? ''));
    this.birthdayLayer?.set(
      birthdayDecorations(
        this.celebrants,
        roster,
        deskAreas(this.mapData),
        cakeSpots(this.mapData),
      ),
      prefersReducedMotion(),
    );
  }

  /** Seasonal theme (spec §6.12): local preview, then `?theme=`, then the server's `themeId`. */
  private applyTheme(): void {
    const preview =
      useSessionStore.getState().themePreview ?? themeParamOverride(window.location.search);
    const reducedMotion = prefersReducedMotion();
    this.theme?.setTheme(shownTheme(this.serverThemeId, preview), reducedMotion);
  }

  private applyWeather(reading: WeatherView | undefined): void {
    const preview = devWeatherOverride(window.location.search, import.meta.env.DEV);
    const weather = preview ? { condition: preview, precipitationMmH: 2, windMs: 4 } : reading;
    const condition = weather?.condition ?? 'unknown';
    const precipitation = weather?.precipitationMmH ?? 0;
    const wind = weather?.windMs ?? 0;
    const key = `${condition}|${precipitation.toFixed(1)}|${wind.toFixed(1)}`;
    if (key === this.weatherKey || !this.lighting || !this.weather) return;
    this.weatherKey = key;
    const reducedMotion = prefersReducedMotion();
    const effects = weatherEffects(condition, precipitation, wind, reducedMotion);
    this.lighting.setWeather(effects);
    this.weather.setEffects(effects);
  }

  private bindRoomState(): void {
    const $ = officeStateCallbacks<PlayerView>(this.room);
    const state: unknown = this.room.state;

    this.disposers.push(
      $(state).players.onAdd((player: PlayerView, sessionId: string) => {
        if (!this.alive()) return;
        const isLocal = sessionId === this.room.sessionId;
        const avatar = new Avatar(this, {
          name: player.name,
          appearanceKey: player.appearanceKey,
          status: player.status,
          activity: player.activity,
          tileX: player.x,
          tileY: player.y,
          isLocal,
          facing: player.dir,
        });
        this.avatars.set(sessionId, avatar);
        this.avatarUsers.set(sessionId, player.userId);
        avatar.setPartyHat(this.celebrants.has(player.userId));
        this.updateOnlineCount();
        if (isLocal) {
          this.seq = player.seq;
          this.facing = player.dir;
          this.cameras.main.startFollow(avatar.container, true, 0.2, 0.2);
        }

        this.disposers.push(
          $(player).onChange(() => {
            if (!this.alive()) return;
            avatar.setName(player.name);
            avatar.setAppearance(player.appearanceKey);
            if (!isLocal) avatar.setFacing(player.dir);
            avatar.setActivity(player.activity);
            if (avatar.status !== player.status) {
              avatar.setStatus(player.status);
              this.updateOnlineCount();
            }
            if (!isLocal) {
              // Anything further than one step is a teleport (e.g. sitting back down): snap.
              const { x, y } = avatar.tile;
              const far = Math.abs(x - player.x) + Math.abs(y - player.y) > 1;
              const stepMs = player.sprinting ? this.sprintStepMs : this.stepMs;
              avatar.moveTo(player.x, player.y, far ? 0 : stepMs, player.sprinting);
              return;
            }
            // Reconcile once the server has processed our latest input.
            if (player.seq >= this.seq) {
              const { x, y } = avatar.tile;
              if (x !== player.x || y !== player.y) avatar.moveTo(player.x, player.y, 0);
            }
          }),
        );
      }),
    );

    this.disposers.push(
      $(state).players.onRemove((_player: PlayerView, sessionId: string) => {
        if (this.disposed) return;
        this.avatars.get(sessionId)?.destroy();
        this.avatars.delete(sessionId);
        this.avatarUsers.delete(sessionId);
        this.updateOnlineCount();
      }),
    );

    // "..." over nearby players who are typing (spec §18); the server only tells us about those.
    this.disposers.push(
      typingEvents.on((event) => {
        if (!this.disposed) this.avatars.get(event.sessionId)?.setTyping(event.typing);
      }),
    );

    // Emotes from the wheel, shown over the player's head.
    this.disposers.push(
      emoteEvents.on((event) => {
        if (!this.disposed) this.avatars.get(event.sessionId)?.showEmote(event.emote);
      }),
    );

    // Speech bubbles for live chat (history replayed on join never produces one).
    this.disposers.push(
      useSessionStore.subscribe((current, previous) => {
        if (!this.disposed && current.lastLiveChat !== previous.lastLiveChat) {
          this.showBubble(current.lastLiveChat);
        }
      }),
    );
  }

  private showBubble(event: ChatEvent | undefined): void {
    if (event?.senderSessionId) this.avatars.get(event.senderSessionId)?.say(event.body);
  }

  private updateOnlineCount(): void {
    const ids = new Set<string>();
    let online = 0;
    for (const [sessionId, avatar] of this.avatars) {
      if (avatar.status === 'offline') continue;
      online += 1;
      const userId = this.avatarUsers.get(sessionId);
      if (userId) ids.add(userId);
    }
    useSessionStore.getState().setOnlineCount(online);
    useSessionStore.getState().setOnlineUserIds(ids);
  }

  private setupInput(): void {
    const keyboard = this.input.keyboard;
    if (!keyboard) throw new Error('Keyboard input unavailable');
    const K = Phaser.Input.Keyboard.KeyCodes;
    const add = (...codes: number[]) => codes.map((code) => keyboard.addKey(code, false, false));
    // The arrow keys always move; the other keys come from the settings and can be rebound.
    const bind = () => {
      const { keys } = useSettingsStore.getState();
      if (this.keys) for (const key of Object.values(this.keys).flat()) keyboard.removeKey(key);
      if (this.interactKey) keyboard.removeKey(this.interactKey);
      if (this.sprintKey) keyboard.removeKey(this.sprintKey);
      this.keys = {
        up: add(K.UP, keys.up.keyCode),
        down: add(K.DOWN, keys.down.keyCode),
        left: add(K.LEFT, keys.left.keyCode),
        right: add(K.RIGHT, keys.right.keyCode),
      };
      [this.interactKey] = add(keys.interact.keyCode) as [Phaser.Input.Keyboard.Key];
      [this.sprintKey] = add(keys.sprint.keyCode) as [Phaser.Input.Keyboard.Key];
    };
    bind();
    this.disposers.push(
      useSettingsStore.subscribe((current, previous) => {
        if (current.keys !== previous.keys && this.alive()) bind();
      }),
    );
  }

  /** Points the "E" prompt at the nearest interactive object, when the tile or facing changed. */
  private updateInteractionTarget(): void {
    const self = this.avatars.get(this.room.sessionId);
    if (!self) return;
    const { x, y } = self.tile;
    const key = `${x},${y},${this.facing}`;
    if (key === this.targetKey) return;
    this.targetKey = key;
    const target = findInteractionTarget(this.interactives, { x, y }, this.facing);
    this.promptBubble?.show(target?.object.footprint);
    useSessionStore.getState().setInteractionTarget(
      target && {
        objectId: target.object.id,
        objectType: target.object.type,
        labelKey: target.labelKey,
      },
    );
  }

  /** Asks the server to use the targeted object; it checks range, cooldown and permission. */
  private interact(): void {
    const target = useSessionStore.getState().interactionTarget;
    if (!target) return;
    // A PIN door asks for its code while locked (the keypad sends it) and otherwise works like a
    // toggle door; players who know the code can also lock and unlock it (ADR-0039).
    if (target.objectType === 'pin_door') {
      this.interactPinDoor(target.objectId);
      return;
    }
    // Objects with several actions (coffee or tea) open a menu instead (ADR-0029).
    const choices = interactionFor(target.objectType)?.choices;
    if (choices) {
      useBoardStore.getState().openMenu({ objectId: target.objectId, choices });
      return;
    }
    // Pressing E again on a door you hold lets go of it (ADR-0038).
    const objects = (
      this.room.state as { objects?: Map<string, { occupants?: Iterable<string> }> } | undefined
    )?.objects;
    const holding =
      target.objectType === 'coop_door' &&
      Array.from(objects?.get(String(target.objectId))?.occupants ?? []).includes(
        this.room.sessionId,
      );
    const message: InteractMessage = holding
      ? { objectId: target.objectId, action: 'release' }
      : { objectId: target.objectId };
    this.room.send(CLIENT_MESSAGES.interact, message);
  }

  private interactPinDoor(objectId: number): void {
    const objects = (this.room.state as { objects?: Map<string, { data?: string }> } | undefined)
      ?.objects;
    const door = parseObjectData<{ unlocked?: boolean }>(objects?.get(String(objectId))?.data);
    const known = usePinDoorStore.getState().known.has(objectId);
    switch (pinDoorPress(door?.unlocked === true, known)) {
      case 'keypad':
        usePinDoorStore.getState().openAt(objectId);
        break;
      case 'menu':
        useBoardStore.getState().openMenu({
          objectId,
          choices: [
            { action: 'use', labelKey: 'pinDoor.toggle' },
            { action: 'lock', labelKey: 'pinDoor.lock' },
          ],
        });
        break;
      case 'unlock':
        this.room.send(CLIENT_MESSAGES.interact, { objectId, action: 'unlock' });
        break;
      case 'use':
        this.room.send(CLIENT_MESSAGES.interact, { objectId });
        break;
    }
  }

  private heldDirection(): Direction | undefined {
    for (const direction of ['up', 'down', 'left', 'right'] as const) {
      if (this.keys[direction].some((key) => key.isDown)) return direction;
    }
    return undefined;
  }

  private applyZoom(): void {
    this.cameras.main.setZoom(integerZoom(this.scale.width, this.scale.height));
    this.bounds = undefined; // the region padding depends on the view size
  }
}

/** Phaser sets `pendingDestroy` on a game whose destruction is queued, but its typings omit it. */
function isTearingDown(game: Phaser.Game): boolean {
  return (game as Phaser.Game & { pendingDestroy?: boolean }).pendingDestroy === true;
}
